/**
 * Pure helpers for the Class Schedule Day / Start-Time / End-Time filters.
 *
 * These filters run CLIENT-SIDE over the rows the list endpoint already returns
 * (the endpoint sends the full date-range set; the page paginates/exports/renders
 * every view from that array — same as the Location/Mode/Enrollment/Type filters).
 *
 * Day matching uses each row's ACTUAL occurrence date (`scheduledDate`, "YYYY-MM-DD")
 * as the source of truth — never a recurring template's configured day — so a
 * session rescheduled to another weekday is matched by the day it actually lands on.
 *
 * Time matching compares the row's normalized 24-hour "HH:mm" database values
 * (`startTime`/`endTime`) against normalized "HH:mm" filter values — never
 * localized display strings. Zero-padded "HH:mm" compares correctly as a string.
 */
import dayjs from "dayjs";

export interface DayOption {
  value: string; // canonical lowercase weekday, e.g. "monday"
  label: string; // display, e.g. "Monday"
}

// Canonical weekday order (Mon→Sun) used for storage, display and URL.
export const DAY_OPTIONS: DayOption[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const DAY_INDEX = new Map(DAY_OPTIONS.map((d, i) => [d.value, i]));
const DAY_LABEL = new Map(DAY_OPTIONS.map((d) => [d.value, d.label]));

/** "monday" → "Monday" (falls back to the raw value). */
export const dayLabel = (value: string): string => DAY_LABEL.get(value) ?? value;

/** Unique, valid, canonically-ordered (Mon→Sun) lowercase weekday values. */
export const normalizeDays = (days: string[] | null | undefined): string[] => {
  if (!Array.isArray(days)) return [];
  const seen = new Set<string>();
  days.forEach((d) => {
    const v = String(d || "").trim().toLowerCase();
    if (DAY_INDEX.has(v)) seen.add(v);
  });
  return Array.from(seen).sort((a, b) => (DAY_INDEX.get(a)! - DAY_INDEX.get(b)!));
};

/** Parse a `days=monday,wednesday` URL param safely (invalid values ignored). */
export const parseDaysParam = (raw: string | null | undefined): string[] =>
  normalizeDays(String(raw || "").split(","));

/** Serialize the selected days to the canonical `monday,wednesday` URL form. */
export const serializeDays = (days: string[]): string => normalizeDays(days).join(",");

/** Normalize any time-ish value to zero-padded 24-hour "HH:mm", or "" if invalid. */
export const hhmm = (v: string | null | undefined): string => {
  if (!v) return "";
  const m = String(v).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return "";
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

/** 24-hour "HH:mm" → 12-hour "h:mm AM/PM" for chips/labels (never used for compares). */
export const fmt12 = (v: string | null | undefined): string => {
  const t = hhmm(v);
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const isPM = h >= 12;
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
};

/** Lowercase weekday derived from an occurrence date string ("YYYY-MM-DD"). */
export const weekdayOf = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  return d.isValid() ? d.format("dddd").toLowerCase() : "";
};

/**
 * A time range is invalid only when BOTH bounds are set and End is not strictly
 * later than Start. Either field alone is always valid.
 */
export const isTimeRangeValid = (start: string, end: string): boolean => {
  const s = hhmm(start);
  const e = hhmm(end);
  if (s && e) return e > s;
  return true;
};

export interface DayTimeCriteria {
  days: string[];   // normalized lowercase weekdays; [] = All Days
  start: string;    // "HH:mm" or ""
  end: string;      // "HH:mm" or ""
}

/**
 * True if a row passes the active Day/Start/End filters.
 *  - Days: Match Any (row's actual weekday ∈ selected set).
 *  - Start: row.startTime >= start.
 *  - End:   row.endTime <= end.
 * When both bounds are set but the range is invalid, time bounds are ignored
 * (the UI surfaces a validation error instead of collapsing the list).
 */
export const rowMatchesDayTime = (
  row: { scheduledDate?: string; startTime?: string; endTime?: string },
  criteria: DayTimeCriteria
): boolean => {
  const days = normalizeDays(criteria.days);
  if (days.length > 0) {
    const wd = weekdayOf(row.scheduledDate);
    if (!wd || !days.includes(wd)) return false;
  }
  const rangeValid = isTimeRangeValid(criteria.start, criteria.end);
  const start = hhmm(criteria.start);
  const end = hhmm(criteria.end);
  if (start && rangeValid) {
    const rs = hhmm(row.startTime);
    if (!rs || rs < start) return false;
  }
  if (end && rangeValid) {
    const re = hhmm(row.endTime);
    if (!re || re > end) return false;
  }
  return true;
};
